import AuthService from '@dhi/react-components';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { decodeToken } from '../helpers/tokenAuth';
import GraphQLService from '../services/GraphQLService';
import { RootStore } from './RootStore';

export interface PortHealthAlert {
  id: string;
  name: string;
  value: string;
  datetime: string;
  description: string | null;
}

const PORT_HEALTH_QUERY = `{
  portHealthScalars(where: {
    _or: [
      { value: { _ilike: "%Warning%" } },
      { value: { _ilike: "%Critical%" } }
    ]
  }) {
    id
    name
    value
    datetime
    description
  }
}`;

const POLL_INTERVAL_MS = 60000;

export class PortHealthStore {
  private allStores: RootStore;
  private authService: AuthService;
  private graphQLService: GraphQLService | null = null;
  private pollTimer: ReturnType<typeof setInterval> | null = null;

  portHealthAlerts: PortHealthAlert[] = [];
  isPolling = false;

  constructor(allStores: RootStore) {
    makeObservable(this, {
      portHealthAlerts: observable,
      isPolling: observable,
      hasActiveAlerts: computed,
      startPolling: action.bound,
      stopPolling: action.bound,
      fetchAlerts: action.bound,
    });

    this.allStores = allStores;
    this.authService = new AuthService(process.env.REACT_APP_API_AUTH_URL);
  }

  get hasActiveAlerts(): boolean {
    return this.portHealthAlerts.length > 0;
  }

  private getGraphQLService(): GraphQLService | null {
    try {
      const session = this.authService.getSession();
      if (!session?.accessToken) return null;
      const claims = decodeToken(session.accessToken);
      const userId = claims?.clientid;
      this.graphQLService = new GraphQLService(session.accessToken, userId);
      return this.graphQLService;
    } catch {
      this.graphQLService = null;
      return null;
    }
  }

  async fetchAlerts(): Promise<void> {
    const service = this.getGraphQLService();
    if (!service) return;

    try {
      const data: any = await service.client.request(PORT_HEALTH_QUERY);
      runInAction(() => {
        this.portHealthAlerts = data.portHealthScalars ?? [];
      });
    } catch (error) {
      console.error('PortHealthStore: Failed to fetch port health alerts', error);
      // Retain existing alerts on error; null out service so token is refreshed next poll
      this.graphQLService = null;
    }
  }

  startPolling(): void {
    if (this.isPolling) return;

    this.isPolling = true;
    this.fetchAlerts();

    this.pollTimer = setInterval(() => {
      this.fetchAlerts();
    }, POLL_INTERVAL_MS);
  }

  stopPolling(): void {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
    this.isPolling = false;
  }
}
